import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Paper, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TemplateTable from './TemplateTable';
import TemplateDialog from './TemplateDialog';
import ManageGoalsDialog from './ManageGoalsDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import FilterPanel from './FilterPanel';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const TemplateManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    
    const templates = useSelector(selectors.selectTemplates);
    const loading = useSelector(selectors.selectLoading);
    const pagination = useSelector(selectors.selectPagination);
    const filters = useSelector(selectors.selectFilters);

    // Local state for search input (immediate UI feedback)
    const [searchInput, setSearchInput] = useState('');
    const debounceTimerRef = useRef(null);

    // Initialize from URL parameters on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchParam = params.get('search') || '';
        const offsetParam = params.get('page') ? parseInt(params.get('page'), 10) : 1;
        const limitParam = params.get('limit') ? parseInt(params.get('limit'), 10) : pagination.limit;

        // Update Redux state with URL params
        if (searchParam !== filters.search) {
            dispatch(actions.setFilters({ search: searchParam }));
        }
        if (offsetParam !== pagination.offset || limitParam !== pagination.limit) {
            dispatch(actions.setPagination({ offset: offsetParam, limit: limitParam }));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync local search input with Redux filter
    useEffect(() => {
        setSearchInput(filters.search);
    }, [filters.search]);

    // Sync URL with current state
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.search) params.set('search', filters.search);
        if (pagination.offset && pagination.offset !== 1) params.set('page', pagination.offset.toString());
        if (pagination.limit && pagination.limit !== 10) params.set('limit', pagination.limit.toString());

        const newSearch = params.toString();
        if (location.search !== `?${newSearch}` && newSearch !== location.search.substring(1)) {
            navigate(`?${newSearch}`, { replace: true });
        }
    }, [filters.search, pagination.offset, pagination.limit, navigate, location.search]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        dispatch(actions.fetchTemplates());
        dispatch(actions.fetchAllGoals());
        dispatch(actions.fetchDepartments());
    }, [dispatch, pagination.limit, pagination.offset, filters.search]);

    const handleCreateTemplate = () => {
        dispatch(actions.openTemplateDialog());
    };

    const handlePageChange = (newOffset) => {
        dispatch(actions.setPagination({ offset: newOffset }));
    };

    const handlePageSizeChange = (newLimit) => {
        dispatch(actions.setPagination({ limit: newLimit, offset: 1 }));
    };

    // Debounced search handler
    const handleSearch = useCallback((event) => {
        const newSearch = event.target.value;
        
        // Update UI immediately (no lag)
        setSearchInput(newSearch);
        
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // Set new timer - wait 500ms after typing stops
        debounceTimerRef.current = setTimeout(() => {
            // Only trigger API if empty (clear) OR >= 2 chars
            if (newSearch === '' || newSearch.length >= 2) {
                dispatch(actions.setFilters({ search: newSearch }));
                // Reset to page 1 when searching
                if (pagination.offset !== 1) {
                    dispatch(actions.setPagination({ offset: 1 }));
                }
            }
        }, 500);
    }, [dispatch, pagination.offset]);

    const handleResetFilters = () => {
        setSearchInput('');
        dispatch(actions.setFilters({ search: '' }));
        dispatch(actions.setPagination({ offset: 1 }));
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Templates
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateTemplate}
                    sx={{ textTransform: 'none' }}
                >
                    Create Template
                </Button>
            </Box>

            <FilterPanel
                search={searchInput}
                onSearch={handleSearch}
                onReset={handleResetFilters}
                totalCount={pagination.total}
            />

            <Paper sx={(theme) => ({ width: '100%', overflow: 'hidden', boxShadow: 'none', border: `1px solid ${theme.palette.divider}` })}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && templates.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            No templates found. Create your first template to get started.
                        </Typography>
                    </Box>
                )}

                {!loading && templates.length > 0 && (
                    <TemplateTable
                        templates={templates}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                )}
            </Paper>

            <TemplateDialog />
            <ManageGoalsDialog />
            <DeleteConfirmationDialog />
        </Box>
    );
};

export default TemplateManagement;
